// Stammdaten der KI-API: Anbieter, Bereiche, Modelle und Modell-Parameter.
// Aus diesen Eintraegen baut die Seite "KI-API" ihre voneinander abhaengigen
// Auswahlfelder (Anbieter -> Bereich -> Modell -> Parameter) auf. Die vier
// Reiter teilen sich eine Tabelle/Dialog-Logik, gesteuert ueber TABS.

import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Container, Modal, Form, Tabs, Tab, Badge } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import AiApiService from "../services/ai-api.service";
import EventBus from "../common/EventBus";
import ErrorModal from "./ErrorModal";

// Technische Werte, die das Backend auswertet (keine fachlichen Auswahllisten).
const OUTPUT_TYPES = [["image", "Bild"], ["text", "Text"]];
const RESPONSE_FORMATS = [["b64", "Base64-Bild"], ["url", "Bild-URL"], ["text", "Text"]];
const PARAM_TYPES = [["text", "Text"], ["number", "Zahl"], ["select", "Auswahl"], ["boolean", "Ja/Nein"]];

const TABS = [
  {
    entity: "providers",
    title: "Anbieter",
    pk: "ai_provider_id",
    columns: [["provider_name", "Name"], ["provider_code", "Schlüssel"], ["base_url", "Basis-URL"]],
    fields: [
      { key: "provider_name", label: "Name", type: "text", required: true },
      { key: "provider_code", label: "Technischer Schlüssel", type: "text", help: "z. B. OPENAI" },
      { key: "base_url", label: "Basis-URL", type: "text", required: true, help: "z. B. https://api.openai.com" },
      { key: "auth_header", label: "Auth-Header", type: "text", required: true, default: "Authorization", help: "z. B. Authorization oder x-api-key" },
      { key: "auth_prefix", label: "Präfix vor dem Schlüssel", type: "text", help: "z. B. „Bearer “ (mit Leerzeichen) oder leer" },
      { key: "extra_headers", label: "Zusätzliche Header (JSON)", type: "json", help: "z. B. {\"anthropic-version\":\"2023-06-01\"}" },
      { key: "sort_order", label: "Reihenfolge", type: "number" },
      { key: "is_active", label: "Aktiv", type: "bool", default: true },
    ],
  },
  {
    entity: "categories",
    title: "Bereiche",
    pk: "ai_category_id",
    columns: [["category_name", "Name"], ["category_code", "Schlüssel"], ["output_type", "Ergebnis"]],
    fields: [
      { key: "category_name", label: "Name", type: "text", required: true, help: "z. B. Bild, Text, Sprache" },
      { key: "category_code", label: "Technischer Schlüssel", type: "text", help: "z. B. IMAGE – das automatische Kartenbild nutzt IMAGE" },
      { key: "output_type", label: "Ergebnisart", type: "static", options: OUTPUT_TYPES, required: true, default: "text" },
      { key: "sort_order", label: "Reihenfolge", type: "number" },
      { key: "is_active", label: "Aktiv", type: "bool", default: true },
    ],
  },
  {
    entity: "models",
    title: "Modelle",
    pk: "ai_model_id",
    columns: [["ai_provider_id", "Anbieter"], ["ai_category_id", "Bereich"], ["model_name", "Name"], ["model_code", "Modellkennung"]],
    fields: [
      { key: "ai_provider_id", label: "Anbieter", type: "ref", ref: "providers", required: true },
      { key: "ai_category_id", label: "Bereich", type: "ref", ref: "categories", required: true },
      { key: "model_name", label: "Anzeigename", type: "text", required: true },
      { key: "model_code", label: "Modellkennung beim Anbieter", type: "text", required: true, help: "z. B. gpt-image-1" },
      { key: "endpoint_path", label: "Endpunkt", type: "text", required: true, help: "relativ zur Basis-URL; {{model}} wird ersetzt" },
      { key: "request_template", label: "Body-Vorlage (JSON)", type: "json", required: true, help: "Platzhalter: {{model}}, {{prompt}}, {{system_prompt}}" },
      { key: "params_path", label: "Parameter-Pfad im Body", type: "text", help: "leer = oberste Ebene, z. B. parameters" },
      { key: "response_path", label: "Antwortpfad", type: "text", required: true, help: "z. B. data.0.b64_json" },
      { key: "response_format", label: "Antwortformat", type: "static", options: RESPONSE_FORMATS, required: true, default: "b64" },
      { key: "default_system_prompt", label: "Standard-System-Prompt", type: "textarea", help: "Vorbelegung für neue Konfigurationen; Platzhalter wie {{company_name}} möglich" },
      { key: "sort_order", label: "Reihenfolge", type: "number" },
      { key: "is_active", label: "Aktiv", type: "bool", default: true },
    ],
  },
  {
    entity: "model-params",
    title: "Modell-Parameter",
    pk: "ai_model_param_id",
    columns: [["ai_model_id", "Modell"], ["param_key", "Schlüssel"], ["param_label", "Bezeichnung"], ["param_type", "Typ"], ["default_value", "Standardwert"]],
    fields: [
      { key: "ai_model_id", label: "Modell", type: "ref", ref: "models", required: true },
      { key: "param_key", label: "Schlüssel im Request", type: "text", required: true, help: "z. B. size" },
      { key: "param_label", label: "Bezeichnung", type: "text", required: true },
      { key: "param_type", label: "Feldtyp", type: "static", options: PARAM_TYPES, required: true, default: "text" },
      { key: "default_value", label: "Standardwert", type: "text" },
      { key: "options", label: "Auswahloptionen (eine pro Zeile)", type: "textarea", help: "nur für Feldtyp „Auswahl“" },
      { key: "description", label: "Hinweis", type: "text" },
      { key: "sort_order", label: "Reihenfolge", type: "number" },
    ],
  },
];

const refLabel = {
  providers: (r) => r.provider_name,
  categories: (r) => r.category_name,
  models: (r) => r.model_name,
};
const refPk = { providers: "ai_provider_id", categories: "ai_category_id", models: "ai_model_id" };
const refColumn = { ai_provider_id: "providers", ai_category_id: "categories", ai_model_id: "models" };

const toForm = (tab, row) => {
  const form = {};
  tab.fields.forEach((f) => {
    const v = row ? row[f.key] : undefined;
    if (f.type === "bool") form[f.key] = row ? !(v === false || v === 0) : f.default !== false;
    else if (f.type === "json" && v) {
      try {
        form[f.key] = JSON.stringify(JSON.parse(v), null, 2);
      } catch (_) {
        form[f.key] = v;
      }
    } else form[f.key] = v === null || v === undefined ? (row ? "" : f.default ?? "") : `${v}`;
  });
  return form;
};

const BoardAiMasterData = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].entity);
  const [data, setData] = useState({ providers: [], categories: [], models: [], "model-params": [] });
  const [editing, setEditing] = useState(null); // { tab, row|null }
  const [form, setForm] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState({ show: false, message: "", code: "" });

  const showApiError = useCallback((err, fallback) => {
    const res = err && err.response;
    setError({ show: true, message: (res && res.data && res.data.message) || fallback, code: res ? `HTTP ${res.status}` : "" });
    if (res && res.status === 401) EventBus.dispatch("logout");
  }, []);

  const reload = useCallback(() => {
    Promise.all(TABS.map((t) => AiApiService.listMasterData(t.entity)))
      .then((results) => {
        const next = {};
        TABS.forEach((t, i) => {
          next[t.entity] = Array.isArray(results[i].data) ? results[i].data : [];
        });
        setData(next);
      })
      .catch((err) => showApiError(err, "Die KI-Stammdaten konnten nicht geladen werden."));
  }, [showApiError]);

  useEffect(() => {
    reload();
  }, [reload]);

  const refName = (column, id) => {
    const ref = refColumn[column];
    const row = (data[ref] || []).find((r) => r[refPk[ref]] === id);
    return row ? refLabel[ref](row) : `#${id}`;
  };

  const openEdit = (tab, row) => {
    setFieldErrors({});
    setForm(toForm(tab, row));
    setEditing({ tab, row });
  };

  const save = () => {
    const { tab, row } = editing;
    const payload = { ...form };
    const errs = {};
    tab.fields.forEach((f) => {
      if (f.required && f.type !== "bool" && !`${payload[f.key] ?? ""}`.trim()) errs[f.key] = "Pflichtfeld.";
      if (f.type === "json" && `${payload[f.key] ?? ""}`.trim()) {
        try {
          JSON.parse(payload[f.key]);
        } catch (_) {
          errs[f.key] = "Kein gültiges JSON.";
        }
      }
    });
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    const request = row
      ? AiApiService.updateMasterData(tab.entity, row[tab.pk], payload)
      : AiApiService.createMasterData(tab.entity, payload);
    request
      .then(() => {
        setEditing(null);
        reload();
      })
      .catch((err) => {
        const res = err && err.response;
        if (res && res.status === 422 && res.data && res.data.errors) setFieldErrors(res.data.errors);
        else showApiError(err, "Der Eintrag konnte nicht gespeichert werden.");
      });
  };

  const remove = (tab, row) => {
    if (!window.confirm("Diesen Eintrag wirklich löschen?")) return;
    AiApiService.deleteMasterData(tab.entity, row[tab.pk])
      .then(reload)
      .catch((err) => showApiError(err, "Der Eintrag konnte nicht gelöscht werden."));
  };

  const renderField = (f) => {
    const value = form[f.key];
    const set = (v) => setForm((prev) => ({ ...prev, [f.key]: v }));
    const invalid = !!fieldErrors[f.key];
    let control;
    if (f.type === "bool") {
      control = <Form.Check type="switch" label={f.label} checked={!!value} onChange={(e) => set(e.target.checked)} />;
    } else if (f.type === "ref" || f.type === "static") {
      const options =
        f.type === "ref"
          ? (data[f.ref] || []).map((r) => [r[refPk[f.ref]], refLabel[f.ref](r)])
          : f.options;
      control = (
        <Form.Select value={value} isInvalid={invalid} onChange={(e) => set(e.target.value)}>
          <option value="">– bitte wählen –</option>
          {options.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </Form.Select>
      );
    } else if (f.type === "textarea" || f.type === "json") {
      control = (
        <Form.Control
          as="textarea"
          rows={f.type === "json" ? 4 : 6}
          className={f.type === "json" ? "font-monospace small" : undefined}
          value={value}
          isInvalid={invalid}
          onChange={(e) => set(e.target.value)}
        />
      );
    } else {
      control = <Form.Control type={f.type === "number" ? "number" : "text"} value={value} isInvalid={invalid} onChange={(e) => set(e.target.value)} />;
    }
    return (
      <Form.Group className="mb-3" key={f.key}>
        {f.type !== "bool" && <Form.Label>{f.label}{f.required ? " *" : ""}</Form.Label>}
        {control}
        {f.help && <Form.Text className="text-muted">{f.help}</Form.Text>}
        {invalid && <div className="invalid-feedback d-block">{fieldErrors[f.key]}</div>}
      </Form.Group>
    );
  };

  return (
    <Container className="mt-4">
      <h3>KI-Stammdaten</h3>
      <p className="text-muted">
        Anbieter, Bereiche, Modelle und Modell-Parameter für die Seite „KI-API“. Ein Bereich erscheint beim Anbieter,
        sobald dort ein aktives Modell dieses Bereichs existiert.
      </p>
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        {TABS.map((tab) => (
          <Tab eventKey={tab.entity} title={`${tab.title} (${(data[tab.entity] || []).length})`} key={tab.entity}>
            <Button className="mb-3" onClick={() => openEdit(tab, null)}>+ {tab.title.replace(/e$/, "")} anlegen</Button>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  {tab.columns.map(([k, l]) => <th key={k}>{l}</th>)}
                  {tab.fields.some((f) => f.key === "is_active") && <th>Status</th>}
                  <th style={{ width: 100 }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {(data[tab.entity] || []).map((row) => (
                  <tr key={row[tab.pk]}>
                    {tab.columns.map(([k]) => (
                      <td key={k} className="text-break">{refColumn[k] ? refName(k, row[k]) : row[k] ?? "–"}</td>
                    ))}
                    {tab.fields.some((f) => f.key === "is_active") && (
                      <td>{row.is_active === false || row.is_active === 0 ? <Badge bg="secondary">inaktiv</Badge> : <Badge bg="success">aktiv</Badge>}</td>
                    )}
                    <td>
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(tab, row)} title="Bearbeiten"><FaEdit /></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => remove(tab, row)} title="Löschen"><FaTrash /></Button>
                    </td>
                  </tr>
                ))}
                {(data[tab.entity] || []).length === 0 && (
                  <tr><td colSpan={tab.columns.length + 2} className="text-center text-muted">Noch keine Einträge.</td></tr>
                )}
              </tbody>
            </Table>
          </Tab>
        ))}
      </Tabs>

      <Modal show={!!editing} onHide={() => setEditing(null)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editing && (editing.row ? "Bearbeiten" : "Anlegen")}: {editing && editing.tab.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{editing && editing.tab.fields.map(renderField)}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Button>
          <Button variant="primary" onClick={save}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      <ErrorModal show={error.show} handleClose={() => setError({ show: false, message: "", code: "" })} errorMessage={error.message} errorCode={error.code} />
    </Container>
  );
};

export default BoardAiMasterData;
